"use client";

import { Fragment, useEffect, useState } from "react";
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from "@headlessui/react";
import clsx from "clsx";
import {
  ArrowDown01Icon,
  Cancel01Icon,
  PreferenceVerticalIcon,
  Search01Icon,
  Tick01Icon,
} from "hugeicons-react";
import { Industry, Province } from "@/modules/types/metadata/metadata";
import { useLocation } from "@/modules/hooks/metadata/useLocation";
import { useClientTranslations } from "@/lib/translate";
import { useRouter, useSearchParams } from "next/navigation";
import { routes } from "@/lib/utils/routes";
import { useMetadata } from "@/modules/hooks/metadata/useMetadata";

export const CompanyFilter = () => {
  const { t: metaT } = useClientTranslations("metadata");
  const router = useRouter();
  const searchParams = useSearchParams();
  const params = new URLSearchParams(searchParams.toString());
  const [isAdvancedFilterOpen, setIsAdvancedFilterOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedProvice, setSelectedProvince] = useState<Province | null>(
    null,
  );
  const [selectedIndustry, setSelectedIndustry] = useState<Industry | null>(
    null,
  );
  const { provinces, searchProvinces, resetProvinces } = useLocation();
  const { industries } = useMetadata();

  const handleClearFilters = () => {
    router.push(routes.company.list);
  };

  useEffect(() => {
    const q = searchParams.get("q");
    if (q && q.length > 0) {
      setSearch(q);
    }
    const prv = searchParams.get("prv");
    if (prv && prv.length > 0) {
      const province = provinces.find((province) => province.codeName === prv);
      if (province) {
        setSelectedProvince(province);
      }
    }
    const ind = searchParams.get("ind");
    if (ind && ind.length > 0) {
      const industry = industries.find((industry) => industry.title === ind);
      if (industry) {
        setSelectedIndustry(industry);
      }
    }
  }, []);

  useEffect(() => {
    const q = searchParams.get("q");
    if (q && q.length > 0) {
      setSearch(q);
    }
    const prv = searchParams.get("prv");
    if (prv && prv.length > 0) {
      const province = provinces.find((province) => province.codeName === prv);
      if (province) {
        setSelectedProvince(province);
      }
    }
    const ind = searchParams.get("ind");
    if (ind && ind.length > 0) {
      const industry = industries.find((industry) => industry.title === ind);
      if (industry) {
        setSelectedIndustry(industry);
      }
    }
  }, [provinces, industries]);

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    params.set("q", search);
    if (selectedProvice) {
      params.set("prv", selectedProvice.codeName);
    }
    if (selectedIndustry) {
      params.set(
        "ind",
        selectedIndustry.slug && selectedIndustry.slug.length > 0
          ? selectedIndustry.slug
          : selectedIndustry.title,
      );
    }
    router.push(`${routes.company.list}?${params.toString()}`);
  };

  return (
    <section className="flex justify-center bg-white shadow-sm">
      <form onSubmit={handleSearch} className="container py-6 px-2 md:px-8">
        <div className="space-y-4">
          {/* Primary Filters Row */}
          <div className="grid grid-cols-12 gap-4">
            {/* Search Input - Wider on desktop */}
            <div className="col-span-12 md:col-span-5">
              <div className="relative">
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search company name..."
                  className="w-full rounded-lg border-none bg-white/5 py-3 pl-10 pr-3 text-sm text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-primary"
                />
                <Search01Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              </div>
            </div>

            {/* Location Combobox */}
            <div className="hidden md:block col-span-12 md:col-span-3">
              <Combobox
                value={selectedProvice}
                onChange={(province) => {
                  setSelectedProvince(province);
                  resetProvinces();
                }}
              >
                <div className="relative">
                  <ComboboxInput
                    className="w-full rounded-lg border-none bg-white/5 py-3 pr-8 pl-3 text-sm text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-primary"
                    displayValue={(province: Province) => province?.fullName}
                    placeholder={metaT("selectProvince")}
                    onChange={(event) => searchProvinces(event.target.value)}
                  />
                  <ComboboxButton className="absolute inset-y-0 right-0 flex items-center px-2">
                    <ArrowDown01Icon className="h-5 w-5 text-gray-400" />
                  </ComboboxButton>
                  <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      {provinces.map((province) => (
                        <ComboboxOption
                          key={province.code}
                          value={province}
                          className={({ active }) =>
                            clsx(
                              "relative cursor-pointer select-none py-2 pl-8 pr-4",
                              active ? "text-primary" : "text-gray-900",
                            )
                          }
                        >
                          {({ selected }) => (
                            <>
                              <span
                                className={
                                  selected
                                    ? "font-semibold text-primary"
                                    : "font-normal"
                                }
                              >
                                {province.fullName}
                              </span>
                              {selected && (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-primary">
                                  <Tick01Icon className="h-5 w-5" />
                                </span>
                              )}
                            </>
                          )}
                        </ComboboxOption>
                      ))}
                    </ComboboxOptions>
                  </Transition>
                </div>
              </Combobox>
            </div>

            {/* Industry Dropdown */}
            <div className="hidden md:block col-span-12 md:col-span-2">
              <Listbox value={selectedIndustry} onChange={setSelectedIndustry}>
                <div className="relative">
                  <ListboxButton className="w-full rounded-lg bg-white py-3 pl-3 pr-8 text-left text-sm text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-primary">
                    <span className="block truncate">
                      {selectedIndustry?.title || "Select industry"}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <ArrowDown01Icon className="h-5 w-5 text-gray-400" />
                    </span>
                  </ListboxButton>
                  <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <ListboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      {industries.map((industry) => (
                        <ListboxOption
                          key={industry.id}
                          value={industry}
                          className={({ active }) =>
                            clsx(
                              "relative cursor-pointer select-none py-2 pl-8 pr-4",
                              active ? "text-primary" : "text-gray-900",
                            )
                          }
                        >
                          {({ selected }) => (
                            <>
                              <span
                                className={
                                  selected
                                    ? "font-semibold text-primary"
                                    : "font-normal"
                                }
                              >
                                {industry.title}
                              </span>
                              {selected && (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-primary">
                                  <Tick01Icon className="h-5 w-5" />
                                </span>
                              )}
                            </>
                          )}
                        </ListboxOption>
                      ))}
                    </ListboxOptions>
                  </Transition>
                </div>
              </Listbox>
            </div>

            {/* Mobile Filter Toggle & Search Button */}
            <div className="col-span-6 md:col-span-2">
              <button
                type="button"
                onClick={() => setIsAdvancedFilterOpen(!isAdvancedFilterOpen)}
                className="w-full md:hidden flex justify-center items-center rounded-lg bg-gray-100 py-3 text-gray-700 hover:bg-gray-200"
              >
                <PreferenceVerticalIcon className="h-5 w-5" />
              </button>
              <button
                type="submit"
                className="hidden md:flex w-full justify-center items-center rounded-lg bg-primary py-3 text-white hover:bg-primaryDark"
              >
                <Search01Icon className="h-5 w-5" />
              </button>
            </div>

            <div className="col-span-6 md:col-span-1">
              <button
                type="submit"
                className="w-full flex justify-center items-center rounded-lg bg-primary py-3 text-white hover:bg-primaryDark md:hidden"
              >
                <Search01Icon className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Advanced Filters Row */}
          <div
            className={clsx(
              "grid gap-4",
              "transition-all duration-300",
              isAdvancedFilterOpen
                ? "grid grid-cols-1 md:grid-cols-12"
                : "hidden",
            )}
          >
            {/* Category Filter */}
            <div className="md:col-span-4">
              <Combobox value={selectedProvice} onChange={setSelectedProvince}>
                <div className="relative">
                  <ComboboxInput
                    className="w-full rounded-lg border-none bg-white/5 py-3 pr-8 pl-3 text-sm text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-primary"
                    displayValue={(province: Province) => province?.fullName}
                    placeholder={metaT("selectProvince")}
                    onChange={(event) => searchProvinces(event.target.value)}
                  />
                  <ComboboxButton className="absolute inset-y-0 right-0 flex items-center px-2">
                    <ArrowDown01Icon className="h-5 w-5 text-gray-400" />
                  </ComboboxButton>
                  <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      {provinces.map((province) => (
                        <ComboboxOption
                          key={province.code}
                          value={province}
                          className={({ active }) =>
                            clsx(
                              "relative cursor-pointer select-none py-2 pl-8 pr-4",
                              active ? "text-primary" : "text-gray-900",
                            )
                          }
                        >
                          {({ selected }) => (
                            <>
                              <span
                                className={
                                  selected
                                    ? "font-semibold text-primary"
                                    : "font-normal"
                                }
                              >
                                {province.fullName}
                              </span>
                              {selected && (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-primary">
                                  <Tick01Icon className="h-5 w-5" />
                                </span>
                              )}
                            </>
                          )}
                        </ComboboxOption>
                      ))}
                    </ComboboxOptions>
                  </Transition>
                </div>
              </Combobox>
            </div>

            {/* Company Size Filter */}
            <div className="md:col-span-4">
              <Listbox value={selectedIndustry} onChange={setSelectedIndustry}>
                <div className="relative">
                  <ListboxButton className="w-full rounded-lg bg-white py-3 pl-3 pr-8 text-left text-sm text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-primary">
                    <span className="block truncate">
                      {selectedIndustry?.title || "Select Industry"}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <ArrowDown01Icon className="h-5 w-5 text-gray-400" />
                    </span>
                  </ListboxButton>
                  <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <ListboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      {industries.map((industry) => (
                        <ListboxOption
                          key={industry.id}
                          value={industry}
                          className={({ active }) =>
                            clsx(
                              "relative cursor-pointer select-none py-2 pl-8 pr-4",
                              active ? "text-primary" : "text-gray-900",
                            )
                          }
                        >
                          {({ selected }) => (
                            <>
                              <span
                                className={
                                  selected
                                    ? "font-semibold text-primary"
                                    : "font-normal"
                                }
                              >
                                {industry.title}
                              </span>
                              {selected && (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-primary">
                                  <Tick01Icon className="h-5 w-5" />
                                </span>
                              )}
                            </>
                          )}
                        </ListboxOption>
                      ))}
                    </ListboxOptions>
                  </Transition>
                </div>
              </Listbox>
            </div>
          </div>

          {/* Clear Filters */}
          <div className="md:col-span-4 flex justify-end items-center">
            <button
              type="button"
              onClick={handleClearFilters}
              className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-2"
            >
              <Cancel01Icon className="h-5 w-5" />
              Clear Filters
            </button>
          </div>
        </div>
      </form>
    </section>
  );
};

CompanyFilter.displayName = "CompanyFilter";
